/*
 * Eagle Leather custom layer for Pillar (FoxEcom Hyper base).
 * Loaded by snippets/el-global.liquid. No core theme file is patched.
 *
 * T02 Add-to-cart hardening
 *  1. Watches the real /cart/add response (fetch wrapper, read-only clone).
 *  2. Success: announces "Added ..." in a polite live region + shows a toast.
 *     If the theme's follow-up step fails silently (its .catch only console.logs),
 *     we refresh the drawer + cart count ourselves via the theme's own
 *     `cart:refresh` event, so the shopper always sees the result.
 *  3. Error (e.g. sold out, 422): stops the theme from opening the drawer over
 *     the inline error, announces it assertively, and moves focus to the message.
 *  4. Network failure: toast with the reason and a link to the cart.
 * 2026-10-01: toast gets a close button, Esc, hover/focus pause, visibility-hidden when closed (no aria-hidden).
 *             Product-page quantity message at the stock cap ("Only N available in this size").
 * 2026-10-02: "Max in cart": add buttons disabled + "Max in cart" when the cart already holds every unit of the
 *             selected size; message "All N in this size are in your cart." (section 4b).
 * 2026-10-02: Pillar 1.5.0 opens the drawer itself after a failed add; blockNextDrawerShow keeps it closed
 *             so the inline error stays in view (the cart:refresh suppression below covers 1.4-style flows).
 */
(function () {
  'use strict';
  if (window.__elGlobal) return;
  window.__elGlobal = true;

  var addUrl = (window.FoxTheme && FoxTheme.routes && FoxTheme.routes.cart_add_url) || '/cart/add';
  var cartUrl = (window.FoxTheme && FoxTheme.routes && FoxTheme.routes.cart_url) || '/cart';
  var FALLBACK_MS = 1500;

  var statusEl = document.getElementById('el-cart-status');
  var alertEl = document.getElementById('el-cart-alert');
  var toastEl = document.getElementById('el-toast');
  var toastTimer = null;
  var suppressDrawerUntil = 0;
  var lastSubmitter = null;

  function t(key, fallback) {
    var s = window.elStrings || {};
    return s[key] || fallback;
  }

  function announce(el, msg) {
    if (!el) return;
    el.textContent = '';
    // Re-set after a tick so repeated identical messages are still read.
    // setTimeout, not rAF: rAF never fires in background tabs, which would drop the message.
    setTimeout(function () { el.textContent = msg; }, 60);
  }

  function hideToast() {
    if (!toastEl) return;
    clearTimeout(toastTimer);
    var hadFocus = toastEl.contains(document.activeElement);
    toastEl.classList.remove('is-visible');
    if (hadFocus && lastSubmitter && document.contains(lastSubmitter)) lastSubmitter.focus();
  }

  function startToastTimer() {
    clearTimeout(toastTimer);
    toastTimer = setTimeout(hideToast, 6000);
  }

  // Visual-only confirmation. Screen readers already hear the live region, so the toast has no role.
  // Closed = visibility:hidden (removes its link from the tab order). Timer pauses while hovered or focused (WCAG 2.2.1).
  function showToast(msg, kind, linkText, linkHref) {
    if (!toastEl) return;
    toastEl.className = 'el-toast el-toast--' + (kind || 'info') + ' is-visible';
    toastEl.innerHTML = '';
    var span = document.createElement('span');
    span.className = 'el-toast__msg';
    span.textContent = msg;
    toastEl.appendChild(span);
    if (linkText && linkHref) {
      var a = document.createElement('a');
      a.href = linkHref;
      a.textContent = linkText;
      toastEl.appendChild(a);
    }
    var x = document.createElement('button');
    x.type = 'button';
    x.className = 'el-toast__close';
    x.setAttribute('aria-label', t('close', 'Close'));
    x.innerHTML = '<span aria-hidden="true">&times;</span>';
    x.addEventListener('click', hideToast);
    toastEl.appendChild(x);
    startToastTimer();
  }

  if (toastEl) {
    toastEl.addEventListener('mouseenter', function () { clearTimeout(toastTimer); });
    toastEl.addEventListener('mouseleave', function () { if (!toastEl.contains(document.activeElement)) startToastTimer(); });
    toastEl.addEventListener('focusin', function () { clearTimeout(toastTimer); });
    toastEl.addEventListener('focusout', function (e) { if (!toastEl.contains(e.relatedTarget)) startToastTimer(); });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && toastEl.classList.contains('is-visible')) hideToast();
    });
  }

  function setButtonState(btn, state) {
    if (!btn) return;
    btn.classList.remove('el-atc--added', 'el-atc--error');
    if (state) {
      btn.classList.add('el-atc--' + state);
      setTimeout(function () { btn.classList.remove('el-atc--' + state); }, 2500);
    }
  }

  function isAddRequest(input) {
    try {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var path = new URL(url, location.origin).pathname.replace(/\.js$/, '');
      return path === addUrl || path === '/cart/add';
    } catch (e) { return false; }
  }

  // ---- 1. Observe /cart/add -------------------------------------------------
  var nativeFetch = window.fetch.bind(window);
  window.fetch = function (input, init) {
    var watching = isAddRequest(input);
    var p = nativeFetch(input, init);
    if (!watching) return p;

    var themeHandled = false;
    var markHandled = function () { themeHandled = true; };
    document.addEventListener('product-ajax:added', markHandled, { once: true });
    document.addEventListener('product-ajax:error', markHandled, { once: true });

    p.then(function (res) {
      return res.clone().json().then(function (data) {
        if (!res.ok || (data && data.status)) return; // error path: handled by listener below
        var title = (data && (data.product_title || data.title)) ||
                    (data && data.items && data.items[0] && data.items[0].product_title) || '';
        var msg = title ? t('added_named', 'Added to cart: ') + title : t('added', 'Added to cart');
        announce(statusEl, msg);
        setButtonState(lastSubmitter, 'added');

        // Visual confirmation only when the drawer did not open (e.g. cart type = page, or theme step failed).
        setTimeout(function () {
          var drawer = document.querySelector('cart-drawer');
          if (!drawer || !drawer.open) showToast(msg, 'success', t('view_cart', 'View cart'), cartUrl);
        }, 700);

        // Theme follow-up failed silently? Refresh drawer + count ourselves.
        setTimeout(function () {
          document.removeEventListener('product-ajax:added', markHandled);
          document.removeEventListener('product-ajax:error', markHandled);
          if (themeHandled) return;
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: true } }));
        }, FALLBACK_MS);
      }).catch(function () { /* non-JSON body: leave to theme */ });
    }).catch(function () {
      document.removeEventListener('product-ajax:added', markHandled);
      document.removeEventListener('product-ajax:error', markHandled);
      var msg = t('network', 'We could not reach the cart. Check your connection and try again.');
      announce(alertEl, msg);
      showToast(msg, 'error', t('view_cart', 'View cart'), cartUrl);
      setButtonState(lastSubmitter, 'error');
    });

    return p;
  };

  // Remember which button submitted (for the visual state).
  document.addEventListener('submit', function (e) {
    var f = e.target;
    if (f && f.matches && f.matches('form[is="product-form"], form[action*="/cart/add"]')) {
      lastSubmitter = e.submitter || f.querySelector('[type="submit"]');
    }
  }, true);

  // ---- 2. Error path: keep the drawer closed, surface the message -----------
  // Pillar 1.5 opens the drawer straight after a failed add (showCartDrawer -> drawer.show), so block that one call.
  function blockNextDrawerShow() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer || drawer.open || typeof drawer.show !== 'function') return;
    var own = Object.prototype.hasOwnProperty.call(drawer, 'show');
    var orig = drawer.show;
    var restore = function () { if (own) drawer.show = orig; else delete drawer.show; };
    drawer.show = function () { restore(); };
    setTimeout(restore, 3000);
  }

  document.addEventListener('product-ajax:error', function (e) {
    suppressDrawerUntil = Date.now() + 3000;
    blockNextDrawerShow();
    var msg = (e.detail && e.detail.errorMessage) || t('error', 'This item could not be added to your cart.');
    announce(alertEl, msg);
    setButtonState(lastSubmitter, 'error');
    var form = lastSubmitter && lastSubmitter.closest('form');
    var box = form && form.querySelector('.product-form__error-message');
    if (box && !box.hidden) {
      box.setAttribute('tabindex', '-1');
      box.scrollIntoView({ block: 'center', behavior: 'smooth' });
      box.focus({ preventScroll: true });
    } else {
      showToast(msg, 'error');
    }
  });

  // Window capture runs before the drawer's document listener.
  window.addEventListener('cart:refresh', function (e) {
    if (Date.now() < suppressDrawerUntil && e.detail) e.detail.open = false;
  }, true);

  // ---- 3. Drawers: dialog semantics (T11 groundwork) -----------------------
  function dialogize(sel, labelSel) {
    var el = document.querySelector(sel);
    if (!el) return;
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-modal', 'true');
    var label = labelSel && el.querySelector(labelSel);
    if (label) {
      if (!label.id) label.id = sel.replace(/[^a-z]/gi, '') + '-title';
      el.setAttribute('aria-labelledby', label.id);
    }
  }
  function initDialogs() {
    dialogize('cart-drawer', 'h2, .drawer__heading, .h4');
    dialogize('menu-drawer');
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initDialogs);
  else initDialogs();

  // ---- 4. Product-page quantity: say why it stops at the stock cap --------
  // snippets/buy-buttons sets data-max/max = stock for tracked, stop-selling variants (snippets/el-qty-max) and
  // data-cart-quantity = units of the variant already in the cart. Cart lines are handled by assets/el-cart.js.
  //
  // 4b. "Max in cart" (EL 2026-10-02). Pillar 1.5.0 (theme.js QuantityInput.setQuantityBoundries) computes the
  // remaining quantity as data-max - data-cart-quantity; at 0 it sets min = value = 0, drops max, and leaves the add
  // button enabled. We wrap that, never fight it: when data-cart-quantity >= data-max the form's add buttons (main +
  // sticky bar) read "Max in cart" and are disabled (disabled + aria-disabled), the + button is disabled, and the
  // .el-qty-msg live region says "All N in this size are in your cart." Pillar refreshes data-cart-quantity / data-max
  // from a section render on every cartUpdate (add, drawer edits/removes) and variant change, then publishes
  // quantityUpdate; we re-check on that, on 'variant:changed', 'product-ajax:added', cartUpdate and on load.
  // Pillar's own button states win: it rewrites the text on variant change ("Add to cart" / "Sold out"), and we only
  // restore our saved text while the button still says "Max in cart". Sold-out variants have no data-max, so they
  // are never treated as maxed and keep Pillar's "Sold out".
  function isCartQty(input) {
    return !!input.closest('cart-items, cart-drawer, .cart-drawer, [data-el-cart]');
  }
  function msgId(input) { return (input.id || 'ElQty') + '-el-msg'; }
  function qtyMsgEl(input) {
    var id = msgId(input);
    var el = document.getElementById(id);
    if (!el) {
      el = document.createElement('p');
      el.id = id;
      el.className = 'el-qty-msg';
      el.setAttribute('aria-live', 'polite');
      var host = input.closest('quantity-input, .quantity') || input;
      host.insertAdjacentElement('afterend', el);
      var d = (input.getAttribute('aria-describedby') || '').split(' ').filter(Boolean);
      if (d.indexOf(id) < 0) { d.push(id); input.setAttribute('aria-describedby', d.join(' ')); }
    }
    return el;
  }
  function maxText() { return t('max_in_cart', 'Max in cart'); }
  // Units of a capped variant already in the cart when that is all of them, else 0.
  function maxedCount(input) {
    var cap = parseInt(input.getAttribute('data-max'), 10);
    var inCart = parseInt(input.getAttribute('data-cart-quantity'), 10) || 0;
    if (!(cap > 0) || inCart < cap) return 0;
    var formId = input.getAttribute('form');
    var form = formId && document.getElementById(formId);
    var idInput = form && form.querySelector('[name="id"]');
    if (idInput && (idInput.disabled || !idInput.value)) return 0; // no purchasable variant selected
    return cap;
  }
  function addButtonsFor(input) {
    var formId = input.getAttribute('form');
    var form = formId && document.getElementById(formId);
    if (!form) return [];
    var list = Array.prototype.slice.call(form.querySelectorAll('button[name="add"]'));
    document.querySelectorAll('button[name="add"][form="' + formId + '"]').forEach(function (b) {
      if (list.indexOf(b) < 0) list.push(b);
    });
    return list;
  }
  function setMaxed(input, n) {
    addButtonsFor(input).forEach(function (b) {
      var span = b.querySelector('.btn__text') || b.querySelector('span');
      if (n) {
        if (!b.hasAttribute('data-el-maxed')) {
          b.setAttribute('data-el-maxed', '');
          b.setAttribute('data-el-text', span ? span.innerHTML : '');
        }
        b.setAttribute('disabled', 'disabled');
        b.setAttribute('aria-disabled', 'true');
        if (span && span.textContent !== maxText()) span.textContent = maxText();
      } else if (b.hasAttribute('data-el-maxed')) {
        b.removeAttribute('aria-disabled');
        if (span && span.textContent === maxText()) {
          span.innerHTML = b.getAttribute('data-el-text') || (window.FoxTheme && FoxTheme.variantStrings && FoxTheme.variantStrings.addToCart) || 'Add to cart';
          b.removeAttribute('disabled');
        }
        b.removeAttribute('data-el-maxed');
        b.removeAttribute('data-el-text');
      }
    });
    var q = input.closest('quantity-input');
    var plus = q && q.querySelector('button[name="plus"]');
    if (plus) {
      if (n) { plus.setAttribute('disabled', ''); plus.setAttribute('data-el-maxed', ''); }
      else if (plus.hasAttribute('data-el-maxed')) {
        plus.removeAttribute('data-el-maxed');
        var mx = parseInt(input.getAttribute('max'), 10);
        if (!(mx > 0) || parseInt(input.value, 10) < mx) plus.removeAttribute('disabled');
      }
    }
  }
  // EL 2026-10-06: 'Only N available' shows only after the shopper hits the cap (user = true). On load and on
  // variant change Pillar's inventory block already says how many are left, so we don't repeat it.
  function checkQty(input, user) {
    if (!input || input.name !== 'quantity' || isCartQty(input)) return;
    var hasSize = !!document.querySelector('variant-selects, variant-radios, .product-form__input');
    var full = input.hasAttribute('form') ? maxedCount(input) : 0;
    if (input.hasAttribute('form')) setMaxed(input, full);
    var msg = '';
    if (full) {
      msg = full === 1
        ? t(hasSize ? 'last_in_cart' : 'last_in_cart_single', hasSize ? 'The last one in this size is in your cart.' : 'The last one is in your cart.')
        : t(hasSize ? 'all_n_in_cart' : 'all_n_in_cart_single', hasSize ? 'All [n] in this size are in your cart.' : 'All [n] are in your cart.').replace('[n]', full);
    } else {
      var max = parseInt(input.getAttribute('max') || input.getAttribute('data-max'), 10);
      var val = parseInt(input.value, 10);
      if (user && max > 0 && !isNaN(val)) {
        if (val > max) { input.value = max; val = max; }
        if (val >= max) msg = t(hasSize ? 'only_n' : 'only_n_single', 'Only [n] available').replace('[n]', max);
      }
    }
    // Only touch the live region when the text changes, so re-checks never re-announce.
    var el = msg ? qtyMsgEl(input) : document.getElementById(msgId(input));
    if (el && el.textContent !== msg) el.textContent = msg;
  }
  document.addEventListener('change', function (e) { if (e.target.matches && e.target.matches('input[name="quantity"]')) checkQty(e.target, true); }, true);
  document.addEventListener('blur', function (e) { if (e.target.matches && e.target.matches('input[name="quantity"]')) checkQty(e.target, true); }, true);
  document.addEventListener('click', function (e) {
    var b = e.target.closest && e.target.closest('quantity-input button[name="plus"], quantity-input button[name="minus"]');
    if (!b) return;
    var input = b.closest('quantity-input').querySelector('input[name="quantity"]');
    setTimeout(function () { checkQty(input, true); }, 0);
  });
  // Say it up front when the selected size is already at its cap (the + button is disabled there, so clicks never fire).
  function checkAllQty() {
    document.querySelectorAll('input[name="quantity"]').forEach(function (input) {
      if (input.hasAttribute('max') || input.hasAttribute('data-max') || document.getElementById(msgId(input)) || input.hasAttribute('form')) checkQty(input);
    });
  }
  var qtyTimer = null;
  // After the current task: Pillar rewrites the add buttons right after these events (toggleAddButton, the sticky
  // bar's variantChange subscriber), so check once they are done.
  function scheduleQty(delay) {
    clearTimeout(qtyTimer);
    qtyTimer = setTimeout(checkAllQty, delay || 0);
  }
  document.addEventListener('variant:changed', function () { scheduleQty(0); });
  document.addEventListener('product-ajax:added', function () { scheduleQty(0); });
  function subscribeQty() {
    var ps = window.FoxTheme && FoxTheme.pubsub;
    if (!ps || !ps.PUB_SUB_EVENTS) return;
    // quantityUpdate is published by Pillar right after it re-reads data-cart-quantity / data-max.
    if (ps.PUB_SUB_EVENTS.quantityUpdate) ps.subscribe(ps.PUB_SUB_EVENTS.quantityUpdate, function () { scheduleQty(0); });
    if (ps.PUB_SUB_EVENTS.cartUpdate) ps.subscribe(ps.PUB_SUB_EVENTS.cartUpdate, function () { scheduleQty(0); });
    if (ps.PUB_SUB_EVENTS.variantChange) ps.subscribe(ps.PUB_SUB_EVENTS.variantChange, function () { scheduleQty(0); });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { subscribeQty(); checkAllQty(); });
  else { subscribeQty(); checkAllQty(); }

  // ---- 5. Scrollable regions must be keyboard-reachable (WCAG 2.1.1) -------
  // Pillar's testimonials masonry scrolls on its own with no focusable content inside.
  function fixScrollRegions() {
    document.querySelectorAll('[id^="testimonials-masonry-"]').forEach(function (el) {
      if (el.scrollHeight <= el.clientHeight + 2 && el.scrollWidth <= el.clientWidth + 2) return;
      if (el.querySelector('a[href], button, input, [tabindex]')) return;
      el.setAttribute('tabindex', '0');
      if (!el.hasAttribute('role')) el.setAttribute('role', 'region');
      if (!el.hasAttribute('aria-label')) el.setAttribute('aria-label', 'Customer reviews');
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fixScrollRegions);
  else fixScrollRegions();
  window.addEventListener('load', fixScrollRegions);
})();
